import React, { Fragment, useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Button,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Eye, Edit, Trash2 } from "react-feather";
import { hasRole } from "../../utils/auth"
import { Breadcrumbs, H5 } from "../../AbstractElements";
import DataTables from "../../Components/Tables/DataTable";
import { titleCase } from "../../utils/case";
import { deletePackage, getPackages } from "../../api/package.api";

const PackageMain = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canViewPackage = hasRole(
    "superadmin",
    "finance",
    "operation"
  );
  const canManagePackages = hasRole(
    "superadmin",
    "finance"
  );
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    perPage: 10,
    totalPages: 1,
  });

  const [sortConfig, setSortConfig] = useState({
    sortBy: "createdAt",
    sortOrder: "DESC",
  });

  const fetchPackages = async (
    page = 1,
    perPage = 10,
    sortBy = sortConfig.sortBy,
    sortOrder = sortConfig.sortOrder
  ) => {
    try {
      setLoading(true);

      const res = await getPackages({
        page,
        perPage,
        sortField: sortBy,
        sortOrder,
      });

      setData(res?.data || []);
      setMeta(
        res?.meta || {
          total: 0,
          page,
          perPage,
          totalPages: 1,
        }
      );
    } catch (err) {
      toast.error(err.message || "failed to fetch packages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages(meta.page, meta.perPage);
  }, []);

  const handlePageChange = (newPage) => {
    fetchPackages(
      newPage,
      meta.perPage,
      sortConfig.sortBy,
      sortConfig.sortOrder
    );
  };

  const handlePerPageChange = (newPerPage) => {
    fetchPackages(
      1,
      newPerPage,
      sortConfig.sortBy,
      sortConfig.sortOrder
    );
  };

  const handleSort = (column, direction) => {
    const sortBy = column.sortField || "createdAt";
    const sortOrder = direction === "asc" ? "ASC" : "DESC";

    setSortConfig({
      sortBy,
      sortOrder,
    });

    fetchPackages(meta.page, meta.perPage, sortBy, sortOrder);
  };

  const getPricing = (pricing = [], season) =>
    pricing.find((p) => p.season === season);

  const formatChargedBy = (value) => {
    switch (value) {
      case "hourly":
        return "/ hour";

      case "nightly":
        return "/ night";

      case "daily":
        return "/ day";

      case "monthly":
        return "/ month";

      case "yearly":
        return "/ year";

      case "flat_fee":
      default:
        return "/ package";
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(titleCase(t("delete this package?")))) {
      return;
    }

    try {
      await deletePackage(id);

      toast.success(titleCase(t("package deleted successfully")));

      fetchPackages(
        meta.page,
        meta.perPage,
        sortConfig.sortBy,
        sortConfig.sortOrder
      );
    } catch (err) {
      toast.error(err.message);
    }
  };

  const columns = [
    {
      name: titleCase(t("package")),
      selector: (row) => row.name,
      sortable: true,
      sortField: "name",
    },
    {
      name: titleCase(t("capacity")),
      selector: row => row.capacity,
      sortable: true,
      sortField: "capacity",
      center: true,
    },
    {
      name: titleCase(t("pricing")),
      grow: 2,
      cell: (row) => {
        const standard = getPricing(row.pricing, "non_peak");
        const peak = getPricing(row.pricing, "peak");

        return (
          <div>
            <div>
              <strong>{titleCase(t("standard"))}:</strong>{" "}
              {standard
                ? `RM ${Number(standard.amount).toFixed(2)} ${formatChargedBy(
                    standard.chargedBy
                  )}`
                : "-"}
            </div>

            <div>
              <strong>{titleCase(t("peak"))}:</strong>{" "}
              {peak
                ? `RM ${Number(peak.amount).toFixed(2)} ${formatChargedBy(
                    peak.chargedBy
                  )}`
                : "-"}
            </div>
          </div>
        );
      },
    },
    {
      name: titleCase(t("status")),
      selector: (row) => row.status,
      center: true,
      sortable: true,
      sortField: "status",
      cell: (row) => (
        <div style={{ width: "100%", textAlign: "center" }}>
          <Badge color={row.status === "active" ? "success" : "dark"}>
            {titleCase(t(row.status || "inactive"))}
          </Badge>
        </div>
      ),
    },
    {
      name: titleCase(t("action")),
      cell: (row) => (
        <div className="d-flex gap-2 align-items-center">
          {canViewPackage && (
            <Eye
              size={18}
              style={{
                cursor: "pointer",
                color: "#7da8c7",
              }}
              title={titleCase(t("view"))}
              onClick={() => navigate(`/packages/view/${row.id}`)}
            />
          )}
          {canManagePackages && (
            <>
              <Edit
                size={18}
                style={{
                  cursor: "pointer",
                  color: "#d4a437",
                }}
                title={titleCase(t("edit"))}
                onClick={() => navigate(`/packages/edit/${row.id}`)}
              />

              <Trash2
                size={18}
                style={{
                  cursor: "pointer",
                  color: "#dc3545",
                }}
                title={titleCase(t("delete"))}
                onClick={() => handleDelete(row.id)}
              />
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("packages"))}
        title={titleCase(t("packages management"))}
        mainTitle={titleCase(t("packages management"))}
      />

      <Container fluid>
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <H5>{titleCase(t("packages list"))}</H5>

                <Button
                  color="success"
                  onClick={() => navigate("/packages/create")}
                >
                  {t("create").toUpperCase()}
                </Button>
              </CardHeader>

              <CardBody>
                <DataTables
                  initialData={data}
                  columns={columns}
                  loading={loading}
                  meta={meta}
                  onPageChange={handlePageChange}
                  onPerPageChange={handlePerPageChange}
                  onSort={handleSort}
                  sortServer
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default PackageMain;